<script lang="ts">
	import { localeRound } from "#lib/utils/math.js";
	import { norwayHourLabel } from "#lib/utils/norwayHour.js";
	import { getDayLabel } from "./_meta";
	import type { DayView } from "#lib/utils/spotprisenViews.js";
	import type { Dag } from "#lib/types/spotPriser.js";
	import type { Omraade } from "#lib/types/omraade.js";

	type Props = {
		day: Dag;
		view: DayView;
		/** Resolved area shown — used in the heading so the info-table
		 *  states which price area the summary covers. */
		area: Omraade;
	};
	let { day, view, area }: Props = $props();

	const lowest = $derived(
		day.priser.length === 0
			? null
			: day.priser.reduce((acc, p) => (p.pris < acc.pris ? p : acc), day.priser[0])
	);
	const highest = $derived(
		day.priser.length === 0
			? null
			: day.priser.reduce((acc, p) => (p.pris > acc.pris ? p : acc), day.priser[0])
	);

	const avgSpot = $derived(
		day.priser.length === 0
			? null
			: day.priser.reduce((sum, p) => sum + p.pris, 0) / day.priser.length
	);
	// Average using the with-strømstøtte price where it applies, otherwise the
	// spot price for that hour. Null when no hour in the day has strømstøtte —
	// the snittpris cell then shows "i.a." (consistent with the per-row cells).
	const hasAnyStromstotte = $derived(day.priser.some((p) => p.hasStromstottePris != null));
	const avgStromstotte = $derived(
		!hasAnyStromstotte || day.priser.length === 0
			? null
			: day.priser.reduce((sum, p) => sum + (p.hasStromstottePris ?? p.pris), 0) / day.priser.length
	);

	// Em-dash as the "no data / not applicable" marker — language-neutral and
	// the standard convention for empty values in data tables. Used in the
	// strømstøtte column for hours where support doesn't kick in (NO4, or
	// hours where the spot price stayed below the support threshold).
	const NA = "—";

	const dateLabel = $derived(
		new Date(day.dato).toLocaleDateString("nb-NO", {
			weekday: "long",
			day: "numeric",
			month: "long",
		})
	);

	const fmt = (n: number) => localeRound(n);
</script>

{#if lowest && highest && avgSpot !== null}
	<div class="cq-bounds">
		<p class="my-0 px-(--table-padding-x)">
			<strong>
				Priser for {area.value}
				{getDayLabel(view)},
				{dateLabel}
			</strong>
		</p>
		<table
			class="shadow-none overflow-initial my-0 w-full [&>*]:w-full text-xs landscape:text-sm tablet:text-xs"
		>
			<thead>
				<tr>
					<th></th>
					<th>kl.</th>
					<th class="text-end whitespace-nowrap">øre/kWh</th>
					<th class="text-end hyphens-manual">med strøm&shy;støtte</th>
				</tr>
			</thead>
			<tbody>
				<tr class="row-low">
					<th scope="row" class="">Laveste pris</th>
					<td class="tabular-nums whitespace-nowrap">{norwayHourLabel(lowest.validOnUtc)}</td>
					<td class="text-end tabular-nums">{fmt(lowest.pris)}</td>
					<td class="text-end tabular-nums">
						{lowest.hasStromstottePris != null ? fmt(lowest.hasStromstottePris) : NA}
					</td>
				</tr>
				<tr class="row-high">
					<th scope="row" class="">Høyeste pris</th>
					<td class="tabular-nums">{norwayHourLabel(highest.validOnUtc)}</td>
					<td class=" text-end tabular-nums">{fmt(highest.pris)}</td>
					<td class="text-end tabular-nums">
						{highest.hasStromstottePris != null ? fmt(highest.hasStromstottePris) : NA}
					</td>
				</tr>
				<tr>
					<th scope="row" class="">Snittpris</th>
					<td class=""></td>
					<td class="text-end tabular-nums">{fmt(avgSpot)}</td>
					<td class="text-end tabular-nums">
						{avgStromstotte !== null ? fmt(avgStromstotte) : NA}
					</td>
				</tr>
			</tbody>
		</table>
	</div>
{/if}

<style lang="postcss">
	.cq-bounds {
		container-type: inline-size;
	}

	p,
	table {
		--table-padding-y: var(--s-1);
		--table-padding-x: var(--s-3);
	}

	tr,
	td {
		vertical-align: bottom;
	}

	/* Match HoursTable's cheapest/most-expensive row tint so the info-table
	   reads as the same data, just summarised. Same recipe — light brand
	   tint behind dark `--natt`-mixed text. */
	.row-low {
		background: color-mix(in oklab, var(--skog) 12%, transparent);
		color: color-mix(in oklab, var(--skog) 30%, var(--natt));
	}
	.row-high {
		background: color-mix(in oklab, var(--naust) 12%, transparent);
		color: color-mix(in oklab, var(--naust) 30%, var(--natt));
	}

	@container (min-width: 25rem) {
		table {
			display: table;
			table-layout: fixed;
		}
	}
</style>
